import { t } from "@lingui/macro";
import {
  Accounting_Centre_Type,
  CreateExpenseItemDescriptionDocument,
  type CreateExpenseItemDescriptionMutation,
  type CreateExpenseItemDescriptionMutationVariables,
  ExpenseBudgetItemType,
  ExpenseBudgetItemTypeEnum,
  ProjectForCreateExpenseDocument,
  type ProjectForCreateExpenseQuery,
  type ProjectForCreateExpenseQueryVariables,
  TaskPositionsForExpenseDocument,
  type TaskPositionsForExpenseQuery,
  type TaskPositionsForExpenseQueryVariables,
} from "@src/__generated__/graphql";
import type { Store } from "@src/components/modules/expenses/Create/store";
import { getColorByStatus } from "@src/components/modules/expenses/listing/table/TableExpenses";
import type { IGroupOption, IOption } from "@src/components/ui-kit";
import type { BudgetPathTagOption } from "@src/components/ui-kit/budget-path-select/types";
import { FetchHelper } from "@src/helpers/apollo/fetch";
import { MutationHelper } from "@src/helpers/apollo/mutation";
import { appStore } from "@src/stores/AppStore";
import { currency } from "@src/utils/formatters";
import { toExpenseDescriptionOptions } from "@src/utils/map-to-options/expense-descriptions";
import { toTagOptions } from "@src/utils/map-to-options/tags";
import { NumberModel, PercentModel } from "@src/utils/models";
import { FieldState } from "formstate";
import { groupBy, map, orderBy } from "lodash";
import { action, computed, makeObservable, observable } from "mobx";
import {
  getItemIdFromBudgetItemId,
  getItemTypeFromBudgetItemId,
  type TBudgetItemForm,
} from "../../form";
import type {
  TAddExpenseItemArgs,
  TProject,
  TProjectsSimple,
} from "../../models";
import { PlannedExpenseMappingStore } from "./planned-expense-mapping-modal/store";

export class BudgetItemStore {
  form: TBudgetItemForm;

  brandInput = new FieldState("");
  budgetItemInput = new FieldState("");
  @observable.ref
  projectsSimple: TProjectsSimple = [];

  @observable.ref
  selectedProject?: TProject;

  /** Last non-empty brand — keeps edit-mode group slot when brand cleared from select. */
  @observable lastGroupedBrandId: string | undefined;

  @observable.ref
  taskPositions: NonNullable<
    TaskPositionsForExpenseQuery["task"]
  >["positions"] = [];

  plannedExpenseMappingStore: PlannedExpenseMappingStore;

  private selectedProjectFetcher = new FetchHelper<
    ProjectForCreateExpenseQuery,
    ProjectForCreateExpenseQueryVariables
  >(ProjectForCreateExpenseDocument);

  private taskPositionsFetcher = new FetchHelper<
    TaskPositionsForExpenseQuery,
    TaskPositionsForExpenseQueryVariables
  >(TaskPositionsForExpenseDocument);

  private expenseItemDescriptionMutator = new MutationHelper<
    CreateExpenseItemDescriptionMutation,
    CreateExpenseItemDescriptionMutationVariables
  >(CreateExpenseItemDescriptionDocument);

  constructor(
    form: TBudgetItemForm,
    readonly expenseStore: Store,
    plannedExpenseUsages: NonNullable<
      TAddExpenseItemArgs["planned_expense_usages"]
    > = [],
    setCostCentre: boolean,
  ) {
    makeObservable(this);
    this.form = form;
    this.lastGroupedBrandId = form.$.brand_id.value || undefined;
    this.plannedExpenseMappingStore = new PlannedExpenseMappingStore(
      this,
      plannedExpenseUsages,
    );

    // Recalc VAT when amount or VAT % changes (DB load uses ctor defaults / reset, not onChange)
    form.$.amount_in_currency.onDidChange(this.recalculateVatInCurrency);
    form.$.vatRate.onDidChange(this.recalculateVatInCurrency);
    form.$.brand_id.onDidChange(({ newValue }) => {
      if (newValue) this.lastGroupedBrandId = newValue;
    });

    if (!form.$.project_id.value) return;
    this.fetchSelectedProject(form.$.project_id.value, setCostCentre);
  }

  @computed get brandIdForGrouping(): string {
    return this.form.$.brand_id.value || this.lastGroupedBrandId || "";
  }

  recalculateVatInCurrency = () => {
    const vatPercent = this.form.$.vatRate.value?.amount ?? 0;
    const amount = this.form.$.amount_in_currency.value.denominatedValue;
    const percentModel = new PercentModel(vatPercent);
    const computed = this.expenseStore.form.total_amount_includes_vat.value
      ? percentModel.vatFromGross(amount)
      : percentModel.percentOf(amount);

    this.form.$.vat_amount_in_currency.onChange(
      this.form.$.vat_amount_in_currency.value.setDenomValue(computed),
    );
  };

  @computed get costCentersOptions() {
    return this.expenseStore.costCentersOptions.filter(
      (option) =>
        !option.isDisabled ||
        option.value === this.form.$.accounting_centre_id.value,
    );
  }

  @computed get vatClassificationsOptions(): IOption[] {
    const selectedClassificationId =
      this.form.$.accounting_vat_classification_id.value;
    const selectedVatRate = this.form.$.vatRate.value;

    return appStore.workspaceStore.accountingVatClassifications
      .filter(
        ({ id, type, deleted_at, vatRate }) =>
          type === Accounting_Centre_Type.Expense &&
          (!deleted_at || id === selectedClassificationId) &&
          typeof vatRate?.amount === "number" &&
          this.expenseStore.selectedVatRates.has(vatRate.amount) &&
          (!selectedVatRate || vatRate.id === selectedVatRate.id),
      )
      .map((classification) => ({
        value: classification.id,
        label: `${classification.centre} - ${classification.title}`,
        disabled: !!classification.deleted_at,
      }));
  }

  @computed get descriptionOptions() {
    return toExpenseDescriptionOptions(
      this.expenseStore.itemDescriptions,
    ).filter(
      (option) =>
        !option.isDisabled || option.value === this.form.$.description_id.value,
    );
  }

  @computed get projectsOptions() {
    return orderBy(
      this.projectsSimple.map((i) => ({
        label: `${i.code} - ${i.title}`,
        value: i.id,
      })),
      ["label"],
      ["asc"],
    );
  }

  @computed get selectedBrandOption() {
    const selectedBrand = this.expenseStore.clients
      .flatMap((client) => client.brands)
      .find(({ id }) => id === this.form.$.brand_id.value);

    if (!selectedBrand) return undefined;

    return {
      value: selectedBrand.id,
      label: `${selectedBrand.code} - ${selectedBrand.name}`,
      tagCode: selectedBrand.code,
    } satisfies BudgetPathTagOption;
  }

  @computed get selectedProjectCode() {
    return (
      this.projectsSimple.find(({ id }) => id === this.form.$.project_id.value)
        ?.code ?? this.selectedProject?.code
    );
  }

  @computed get selectedProjectOption() {
    const selectedProject =
      this.projectsSimple.find(
        ({ id }) => id === this.form.$.project_id.value,
      ) ??
      (this.selectedProject?.id === this.form.$.project_id.value
        ? this.selectedProject
        : undefined);

    if (!selectedProject) return undefined;

    const shortProjectCode = this.removeCodePrefix(
      selectedProject.code,
      this.selectedBrandOption?.tagCode,
    );

    return {
      value: selectedProject.id,
      label: `${selectedProject.code} - ${selectedProject.title}`,
      tagCode: shortProjectCode ?? selectedProject.code,
      tagSuffix: selectedProject.title,
    } satisfies BudgetPathTagOption;
  }

  protected getLabel(item: NonNullable<TProject>["expenses"][0]): string {
    let label = `${item.item_code} - ${item.title}`;

    if (item.stats.planned_expenses) {
      const remaining = currency.formatByWorkspace(
        item.stats.planned_expenses - item.stats.real_expenses,
      );
      label += t` (remaining budget: ${remaining})`;
    }

    return label;
  }

  @computed get showTrackHours() {
    return (
      this.form.$.budget_item_type.value ===
      ExpenseBudgetItemTypeEnum.OurWorkBudgetItem
    );
  }

  @computed get budgetItemId(): string | undefined {
    return getItemIdFromBudgetItemId(this.form.$.budget_item_id.value);
  }

  @computed
  get uninvoicedAmount(): number {
    if (this.expenseStore.form.total_amount_includes_vat.value) {
      return this.expenseStore.form.total_amount_with_vat_in_currency.value
        .denominatedValue;
    }

    return Array.from(this.expenseStore.form.expense_vat_items).reduce(
      (previousValue, [_, item]) =>
        previousValue + item.amount_in_currency.value.denominatedValue,
      0,
    );
  }

  @action.bound
  handlePercentChange(value: string) {
    NumberModel.setFieldState(this.form.$.amount_in_percent, value);
    this.expenseStore.clearPlannedExpenseMappingsError();
  }

  @action.bound
  handleAmountChange(value: string) {
    NumberModel.setFieldState(this.form.$.amount_in_currency, value);
    this.expenseStore.clearPlannedExpenseMappingsError();
  }

  @computed get hasPlannedExpenseMappings(): boolean {
    return this.plannedExpenseMappingStore.hasPersistedUsages;
  }

  @computed get canOpenPlannedExpenseMapping(): boolean {
    return (
      this.plannedExpenseMappingStore.shouldShowTrigger ||
      this.plannedExpenseMappingStore.hasPersistedUsages
    );
  }

  @computed get plannedExpenseMappingStatus(): "empty" | "valid" | "invalid" {
    if (!this.plannedExpenseMappingStore.requiresMapping) {
      return "empty";
    }

    return this.plannedExpenseMappingStore.hasValidPersistedMappings
      ? "valid"
      : "invalid";
  }

  @computed get showStatusIcon() {
    return (
      this.selectedExpenseItem &&
      getColorByStatus(this.selectedExpenseItem.payment_status) !== "white"
    );
  }

  @computed get isItemClosed(): boolean {
    if (this.selectedProject?.finished_at) return true;
    if (
      this.selectedExpenseItem &&
      this.selectedExpenseItem.type !== ExpenseBudgetItemType.Other &&
      this.selectedExpenseItem.completed_at
    ) {
      return true;
    }
    return false;
  }

  @computed get isLinkedToLockedTimeBilling(): boolean {
    const expenseItemId = this.form.$.id.value;
    if (!expenseItemId) return false;

    const expenseItem = this.expenseStore.expenseItemStructured
      ?.flatMap((structuredItem) => structuredItem.expense_items)
      .find((item) => item.id === expenseItemId);

    return expenseItem?.timeBillings?.some((tb) => tb.is_locked) ?? false;
  }

  @computed get linkedTimeBillingData() {
    const expenseItemId = this.form.$.id.value;
    if (!expenseItemId) return null;

    const expenseItem = this.expenseStore.expenseItemStructured
      ?.flatMap((structuredItem) => structuredItem.expense_items)
      .find((item) => item.id === expenseItemId);

    const lockedTimeBilling = expenseItem?.timeBillings?.find(
      (tb) => tb.is_locked,
    );
    if (!lockedTimeBilling) return null;

    return {
      timeBillingId: lockedTimeBilling.id,
      projectId: expenseItem?.project_id,
    };
  }

  @computed get expensesOptions() {
    const options: IGroupOption[] = [];
    const ourWork = this.selectedProject?.ourWorkBudgetItems;
    const expensesGrouped = groupBy(
      this.selectedProject?.expenses ?? [],
      "category.name",
    );

    options.push({
      label: t`Our work`,
      options: map(ourWork, ({ id, item_code, title, stats }) => {
        const remaining = currency.formatByWorkspace(stats.remaining_budget);

        return {
          label: `${item_code} - ${title}${t` (remaining budget: ${remaining})`}`,
          value: `${id}-${ExpenseBudgetItemTypeEnum.OurWorkBudgetItem}`,
        };
      }),
    });

    map(expensesGrouped, (expenseItem, category) => {
      options.push({
        label: category,
        options: expenseItem.map((item) => ({
          label: this.getLabel(item),
          disabled:
            !!item.completed_at && item.type === ExpenseBudgetItemType.Regular,
          value: `${item.id}-${ExpenseBudgetItemTypeEnum.ExpenseBudgetItem}`,
        })),
      });
    });

    return options;
  }

  @computed get selectedBudgetItemOption() {
    const selectedBudgetItemType = getItemTypeFromBudgetItemId(
      this.form.$.budget_item_id.value,
    );
    const option = this.findGroupedOption(
      this.expensesOptions,
      this.form.$.budget_item_id.value,
    );

    if (!option) return undefined;

    const budgetItem =
      selectedBudgetItemType === ExpenseBudgetItemTypeEnum.OurWorkBudgetItem
        ? this.selectedOurWorkBudgetItem
        : this.selectedExpenseItem;
    const optionCode = option.label.split(" - ")[0] ?? option.label;
    const itemCode = budgetItem?.item_code ?? optionCode;
    const shortItemCode = this.removeCodePrefix(
      itemCode,
      this.selectedProjectCode,
    );
    const optionTitle = option.label.split(" - ").slice(1).join(" - ");
    const itemTitle = budgetItem?.title ?? optionTitle ?? undefined;

    return {
      value: option.value,
      label: option.label,
      tagCode: shortItemCode ?? itemCode,
      tagSuffix: itemTitle,
    } satisfies BudgetPathTagOption;
  }

  @computed get selectedExpenseItem() {
    if (
      this.form.$.budget_item_type.value !==
      ExpenseBudgetItemTypeEnum.ExpenseBudgetItem
    ) {
      return undefined;
    }

    return this.selectedProject?.expenses.find(
      (i) => i.id === getItemIdFromBudgetItemId(this.form.$.budget_item_id.$),
    );
  }

  @computed get selectedOurWorkBudgetItem() {
    if (
      this.form.$.budget_item_type.value !==
      ExpenseBudgetItemTypeEnum.OurWorkBudgetItem
    ) {
      return undefined;
    }

    return this.selectedProject?.ourWorkBudgetItems?.find(
      (item) =>
        item.id === getItemIdFromBudgetItemId(this.form.$.budget_item_id.$),
    );
  }

  @computed get positionOptions(): IOption[] {
    const options: IOption[] = [];

    for (const { timeTrackingWorkType } of this.taskPositions) {
      const { id, title } = timeTrackingWorkType;

      if (options.some(({ value }) => value === id)) continue;

      options.push({
        value: id,
        label: title,
      });
    }

    return options;
  }

  @computed get isLoadingTaskPositions(): boolean {
    return this.taskPositionsFetcher.isLoading.value;
  }

  @action
  setProjectsSimple(data: TProjectsSimple) {
    this.projectsSimple = data;
  }

  @action.bound
  clearProjectsSimple() {
    this.projectsSimple = [];
  }

  @action
  setSelectedProject(data: TProject) {
    this.selectedProject = data;
  }

  @action.bound
  clearSelectedProject() {
    this.selectedProject = undefined;
  }

  @action.bound
  async fetchTaskPositions(taskId?: string) {
    if (!taskId) {
      this.taskPositions = [];
      return;
    }

    const [data, error] = await this.taskPositionsFetcher.fetch(
      {
        id: taskId,
      },
      null,
    );

    if (error || !data.task) {
      this.taskPositions = [];
      return;
    }

    this.taskPositions = data.task.positions;
  }

  @action.bound
  handleSelectBrand(value: string) {
    this.form.$.brand_id.onChange(value);
    this.form.$.project_id.reset("");
    this.form.$.budget_item_id.reset("");
    this.clearProjectsSimple();
    this.clearSelectedProject();
    this.syncApprovalWithBudgetPath();
    this.resetPlannedExpenseMappings();
    this.expenseStore.fetchExchangeRate({
      clientId: this.expenseStore.getClientIdFromBrand(value),
    });
  }

  @computed
  get isLoadingSelectedProject(): boolean {
    return this.selectedProjectFetcher.isLoading.value;
  }

  @action.bound
  async fetchSelectedProject(projectId: string, setCostCentre: boolean) {
    const [data, error] = await this.selectedProjectFetcher.fetch(
      {
        id: projectId,
      },
      null,
    );

    if (error || !data.projectForExpense) return;

    this.setSelectedProject(data.projectForExpense);

    if (!setCostCentre) return;
    if (!data.projectForExpense.team.expense_cost_centre_id) return;

    this.form.$.accounting_centre_id.onChange(
      data.projectForExpense.team.expense_cost_centre_id,
    );
  }

  @action.bound
  handleSelectProject(value: string) {
    this.form.$.project_id.onChange(value);
    this.form.$.budget_item_id.reset("");
    this.clearSelectedProject();
    this.syncApprovalWithBudgetPath();
    this.resetPlannedExpenseMappings();

    const foundProject = this.projectsSimple.find(({ id }) => id === value);
    const brandId = foundProject?.brand_id;
    const projectManagerId = foundProject?.project_manager_id;

    !this.form.$.brand_id.value &&
      brandId &&
      this.form.$.brand_id.onChange(brandId);
    projectManagerId && this.form.$.responsible_id.onChange(projectManagerId);
    this.fetchSelectedProject(value, true);
  }

  @action.bound
  handleBudgetItemChange(value: string) {
    this.resetPlannedExpenseMappings();
    this.form.$.budget_item_id.onChange(value);
    this.form.$.budget_item_type.onChange(
      this.expenseStore.form.getItemTypeFromId(value),
    );
    this.syncApprovalWithBudgetPath();
    this.expenseStore.clearPlannedExpenseMappingsError();
    this.resetTrackingValues();

    if (
      this.expenseStore.form.getItemTypeFromId(value) ===
      ExpenseBudgetItemTypeEnum.OurWorkBudgetItem
    )
      return;

    this.handleTrackHoursChange(false);
  }

  @action.bound
  handleRemoveBrand() {
    this.form.$.brand_id.reset("");
    this.form.$.project_id.reset("");
    this.form.$.budget_item_id.reset("");
    this.clearProjectsSimple();
    this.clearSelectedProject();
    this.syncApprovalWithBudgetPath();
    this.expenseStore.fetchExchangeRate();
    this.resetPlannedExpenseMappings();
    this.expenseStore.clearPlannedExpenseMappingsError();
  }

  @action.bound
  handleRemoveProject() {
    this.form.$.project_id.reset("");
    this.form.$.budget_item_id.reset("");
    this.clearSelectedProject();
    this.syncApprovalWithBudgetPath();
    this.resetPlannedExpenseMappings();
    this.expenseStore.clearPlannedExpenseMappingsError();
  }

  @action.bound
  handleRemoveBudgetItemSelection() {
    this.form.$.budget_item_id.reset("");
    this.syncApprovalWithBudgetPath();
    this.resetPlannedExpenseMappings();
    this.expenseStore.clearPlannedExpenseMappingsError();
  }

  @computed get hasCompleteBudgetPath(): boolean {
    return (
      !!this.form.$.brand_id.value &&
      !!this.form.$.project_id.value &&
      !!this.form.$.budget_item_id.value
    );
  }

  /** Auto-approve workspaces approve once the whole budget path is picked, others stay manual. */
  @action.bound
  syncApprovalWithBudgetPath() {
    const autoApprove =
      appStore.workspaceStore.settings?.automatically_approved_expense_items ??
      false;

    const approved = autoApprove && this.hasCompleteBudgetPath;
    this.form.$.approved.onChange(approved);

    if (!approved || this.form.$.responsible_id.value) return;

    this.form.$.responsible_id.onChange(appStore.authStore.user?.id);
  }

  @action.bound
  handleTrackHoursChange(value: boolean) {
    this.form.$.track_hours.onChange(value);

    if (value && this.expenseStore.form.date_of_supply.value) {
      this.form.$.report_date.onChange(
        this.expenseStore.form.date_of_supply.value,
      );
    }

    if (value) return;

    this.resetTrackingValues();
  }

  @action.bound
  async handleCreateNewTag(title: string) {
    const tag = await appStore.tagStore.createTag(title);
    if (!tag) return;

    if (this.form.$.tag_ids.value?.length) {
      this.form.$.tag_ids.onChange(this.form.$.tag_ids.value.concat(tag.id));
    } else {
      this.form.$.tag_ids.onChange([tag.id]);
    }

    return toTagOptions([tag])[0];
  }

  @action.bound
  async handleCreateNewDescription(title: string) {
    const [data, error] = await this.expenseItemDescriptionMutator.mutate({
      title,
    });

    if (error || !data?.createExpenseItemDescription) {
      console.error(error);
      return;
    }
    const newDescriptionOption = toExpenseDescriptionOptions([
      data.createExpenseItemDescription,
    ])[0];

    this.expenseStore.itemDescriptions =
      this.expenseStore.itemDescriptions.concat(
        data.createExpenseItemDescription,
      );

    this.form.$.description_id.onChange(newDescriptionOption.value);
    return newDescriptionOption;
  }

  private findGroupedOption(
    options: IGroupOption[],
    value?: string,
  ): IOption | undefined {
    if (!value) return undefined;

    for (const group of options) {
      const option = group.options.find((item) => item.value === value);

      if (option) return option;
    }

    return undefined;
  }

  private removeCodePrefix(code?: string, prefix?: string) {
    if (!code) return undefined;
    if (!prefix) return code;

    const normalizedPrefix = `${prefix}-`;

    return code.startsWith(normalizedPrefix)
      ? code.slice(normalizedPrefix.length)
      : code;
  }

  @action.bound
  private resetTrackingValues() {
    this.form.$.tracked_time.reset();
    this.form.$.time_tracking_work_type_id.reset();
    this.form.$.report_date.reset();
    this.form.$.task_id.reset("");
    this.taskPositions = [];
  }

  @action.bound
  async syncPlannedExpenseMappings() {
    await this.plannedExpenseMappingStore.sync();
  }

  @action.bound
  resetPlannedExpenseMappings() {
    this.plannedExpenseMappingStore.reset();
  }

  @action.bound
  handleSavePlannedExpenseMappings(
    usages: NonNullable<TAddExpenseItemArgs["planned_expense_usages"]> = [],
  ) {
    this.plannedExpenseMappingStore.setPersistedUsages(usages);
    this.expenseStore.clearPlannedExpenseMappingsError();
  }

  @action.bound
  openPlannedExpenseMapping() {
    this.plannedExpenseMappingStore.modalState.onOpen({
      onSave: this.handleSavePlannedExpenseMappings,
    });
  }

  @action.bound
  handleVatClassificationChange(value: string) {
    this.form.$.accounting_vat_classification_id.onChange(value);

    if (this.form.$.vatRate.value) return;

    const classification =
      appStore.workspaceStore.accountingVatClassifications.find(
        ({ id }) => id === value,
      );
    if (!classification?.vatRate) return;

    const matchingVatRate = this.expenseStore.vatRates.find(
      ({ id }) => id === classification.vatRate?.id,
    );
    if (!matchingVatRate) return;
    this.form.$.vatRate.onChange(matchingVatRate);
  }
}
